import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarCheck, Link2, Unlink, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function GoogleSettingsPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/calendar/connections"); setData(data); }
    catch (e) { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const st = params.get("status");
    if (st === "connected") { toast.success("Google Calendar collegato correttamente"); setParams({}); load(); }
    else if (st === "error") { toast.error("Collegamento Google Calendar non riuscito. Riprova."); setParams({}); }
    else if (st === "scope_missing") { toast.error("Permesso Google Calendar NON concesso", { description: "Ricollega l'account e, nella schermata Google, spunta il permesso sui calendari.", duration: 12000 }); setParams({}); load(); }
  }, [params, setParams, load]);

  const connect = async (consultantId) => {
    setBusy(consultantId);
    try {
      const { data } = await api.get("/oauth/calendar/authorize", { params: { consultant_id: consultantId } });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Integrazione non configurata");
      setBusy("");
    }
  };

  const disconnect = async (consultantId) => {
    setBusy(consultantId);
    try { await api.post(`/calendar/connections/${consultantId}/disconnect`); toast.success("Calendario scollegato"); await load(); }
    catch (e) { toast.error("Errore durante lo scollegamento"); } finally { setBusy(""); }
  };

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="google-settings-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><CalendarCheck className="h-6 w-6 text-brand" /> Google Calendar</h1>
        <p className="mt-1 text-sm text-slate-500">Collega il Google Calendar dei consulenti (OAuth 2.0). Le credenziali restano riservate alla Direzione; nessuna password memorizzata.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !data ? (
        <div className="ds-card p-6 text-sm text-slate-500">Impossibile caricare lo stato.</div>
      ) : (
        <>
          {!data.configured && (
            <div className="ds-card flex items-start gap-3 border-l-4 border-l-warning p-4" data-testid="google-not-configured">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <div className="text-sm text-slate-700">
                <div className="font-semibold">Integrazione non ancora configurata</div>
                I secret <code>GOOGLE_CLIENT_ID</code> e <code>GOOGLE_CLIENT_SECRET</code> non sono impostati. Una volta inseriti nei secret dell'app, i pulsanti "Collega" diventeranno operativi.
              </div>
            </div>
          )}
          <div className="ds-card divide-y divide-slate-100">
            {data.connections.map((c) => (
              <div key={c.consultant_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`google-conn-${c.consultant_id}`}>
                <div>
                  <div className="font-semibold text-slate-800">{c.consultant_name}</div>
                  <div className="text-xs text-slate-500">{c.consultant_email || "—"}</div>
                  <div className="mt-1 text-sm">
                    {c.status === "connected" ? (
                      <span className="inline-flex items-center gap-1 font-medium text-success" data-testid={`google-status-${c.consultant_id}`}>
                        <CheckCircle2 className="h-4 w-4" /> Collegato{c.google_email ? ` · ${c.google_email}` : ""}
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 font-medium text-slate-500" data-testid={`google-status-${c.consultant_id}`}>
                          <Unlink className="h-4 w-4" /> Non collegato
                        </span>
                        {c.last_error && <span className="ml-2 text-xs text-danger" data-testid={`google-error-${c.consultant_id}`}>· {c.last_error.startsWith("Permesso") ? c.last_error : "token scaduto/revocato — ricollega il calendario"}</span>}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {c.status === "connected" ? (
                    <Button variant="outline" disabled={busy === c.consultant_id} onClick={() => disconnect(c.consultant_id)} data-testid={`google-disconnect-${c.consultant_id}`}>
                      <Unlink className="mr-1.5 h-4 w-4" /> Scollega
                    </Button>
                  ) : (
                    <Button disabled={!data.configured || busy === c.consultant_id} onClick={() => connect(c.consultant_id)} data-testid={`google-connect-${c.consultant_id}`}>
                      {busy === c.consultant_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="mr-1.5 h-4 w-4" /> Collega</>}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
